import { useState, useEffect, useCallback } from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import posthog, { DisplaySurveyType } from 'posthog-js';

import { ButtonV2 } from '@restworld/ui-ds';

const POSTHOG_SURVEY_ID = '019c28ed-4f7c-0000-7fdd-70e21d707a0d';

function DashboardFeedbackCard() {
  const { t } = useTranslation('dashboard-page');
  const [showBackdrop, setShowBackdrop] = useState(false);

  const dismissBackdrop = useCallback(() => {
    setShowBackdrop(false);
  }, []);

  // Watch for PostHog survey dismissal/submission to remove backdrop
  useEffect(() => {
    if (!showBackdrop) return;

    const observer = new MutationObserver(() => {
      const surveyPopover = document.querySelector('.PostHogSurvey');
      if (!surveyPopover) {
        dismissBackdrop();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [showBackdrop, dismissBackdrop]);

  const handleClick = () => {
    if (!posthog.__loaded) {
      // eslint-disable-next-line no-console
      console.warn('[DashboardFeedbackCard] PostHog not yet initialized');
      return;
    }
    setShowBackdrop(true);
    posthog.displaySurvey(POSTHOG_SURVEY_ID, {
      displayType: DisplaySurveyType.Popover,
      ignoreConditions: true,
      ignoreDelay: true,
    });
  };

  return (
    <>
      <CardContainer>
        <TextWrapper>
          <CardTitle>{t('your_opinion_matters')}</CardTitle>
          <CardSubtitle>{t('tell_us_how_to_improve')}</CardSubtitle>
        </TextWrapper>
        <ButtonV2 variant="tertiary" StartIcon={MegaphoneIcon} fullwidth onClick={handleClick}>
          {t('leave_feedback')}
        </ButtonV2>
      </CardContainer>
      {showBackdrop && <Backdrop />}
    </>
  );
}

export default DashboardFeedbackCard;

const CardContainer = styled.div`
  ${tw`flex h-full min-h-[200px] flex-col justify-between rounded-2xl bg-neutral-10 pt-10 px-5 pb-5 border border-neutral-40 shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)]`}
`;
const TextWrapper = styled.div`
  ${tw`flex flex-col gap-1`}
`;
const CardTitle = styled.span`
  ${tw`text-base font-semibold text-neutral-700`}
`;
const CardSubtitle = styled.span`
  ${tw`text-base font-normal text-neutral-100`}
`;
const Backdrop = styled.div`
  ${tw`fixed inset-0 z-[90] bg-black/50`}
`;
